import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AuthPage from "./components/pensense_core/AuthPage";
import Dashboard from "./components/pensense_core/Dashboard";
import ActivationSteps from "./components/pensense_core/ActivationSteps";
import StudentOnboarding from "./components/pensense_core/StudentOnboarding";
import Layout from "./components/pensense_core/Layout";
import SchoolPresentation from "./components/pensense_core/SchoolPresentation";
import BPlanPresentation from "./components/pensense_core/BPlanPresentation";
import SuperAdminPanel from "./components/pensense_core/SuperAdminPanel";
import { ThemeProvider } from "./context/ThemeContext";

// LMS Components
import LMSLayout from "./components/lms/LMSLayout";
import LMSDashboard from "./components/lms/LMSDashboard";
import CMS from "./components/lms/CMS";
import CMSTest from "./components/lms/CMSTest";

import StudentDashboard from "./components/lms/StudentDashboard";
import TeacherPanel from "./components/lms/TeacherPanel";
import TestEngine from "./components/lms/TestEngine";
import LiveTest from "./components/lms/LiveTest";
import Analytics from "./components/lms/Analytics";
import Notifications from "./components/lms/Notifications";
import ParentDashboard from "./components/lms/ParentDashboard";
import Payments from "./components/lms/Payments";
import Batches from "./components/lms/Batches";
import DoubtSolving from "./components/lms/DoubtSolving";
import Personalization from "./components/lms/Personalization";
import UserManagement from "./components/lms/UserManagement";
import LiveMeeting from "./components/lms/LiveMeeting";
import UpcomingClasses from "./components/lms/UpcomingClasses";
import Enquiries from "./components/lms/Enquiries";
import CBATest from "./components/lms/CBATest";
import SuperTenantDashboard from "./components/lms/SuperTenantDashboard";
import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Global Axios Interceptor for Session Tracking & Tenancy
axios.interceptors.request.use((config) => {
  const userStr = localStorage.getItem("user");
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
      const activeTenant = user.impersonatedTenantEmail || user.tenantEmail;
      if (activeTenant) {
        config.headers['X-Tenant-ID'] = activeTenant;
      }
    } catch (e) {
      console.error("Error parsing user from localStorage", e);
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

axios.interceptors.response.use((response) => {
  return response;
}, (error) => {
  if (error.response && error.response.status === 401) {
    console.warn("Unauthorized request detected!", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response.status,
      data: error.response.data
    });
    console.warn("Redirecting to login...");
    localStorage.removeItem("user");
    window.location.href = "/";
  }
  return Promise.reject(error);
});

function getStoredUser() {
  try {
    const userStr = localStorage.getItem("user");
    if (!userStr || userStr === "null" || userStr === "undefined") {
      return null;
    }
    return JSON.parse(userStr);
  } catch (e) {
    console.error("Error parsing user from localStorage", e);
    return null;
  }
}

function ProtectedRoute({ children }) {
  const [user, setUser] = useState(getStoredUser);

  useEffect(() => {
    // Re-check on storage changes (e.g. logout from another tab)
    const onStorage = () => setUser(getStoredUser());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isAuthenticated = Boolean(user?.email && user?.role);
  return isAuthenticated ? children : <Navigate to="/" replace />;
}

function StudentRoute({ children }) {
  const [user, setUser] = useState(getStoredUser);

  useEffect(() => {
    // Initial user setup
    const initialUser = getStoredUser();
    setUser(initialUser);
    
    // Stale session check: If a user exists but has no token, force a clear/re-login
    // This is necessary because we just introduced JWT persistence.
    if (initialUser && !initialUser.token) {
      console.warn("Stale session detected (missing JWT). Clearing user...");
      localStorage.removeItem("user");
      setUser(null);
    }

    const onStorage = () => setUser(getStoredUser());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isStudent = Boolean(user?.email && user?.role === "student");
  return isStudent ? children : <Navigate to="/dashboard" replace />;
}

function DemoHandler() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "true") {
      const demoUser = {
        email: "demo@cms.com",
        name: "Demo Admin",
        role: "institute",
        tenantEmail: "cms.com",
        status: "success"
      };
      localStorage.setItem("user", JSON.stringify(demoUser));
      // Hard redirect to clear state and pick up the new user
      window.location.href = "/cms.com/lms/cms";
    }
  }, []);
  return null;
}

function App() {
  const [isInitializing, setIsInitializing] = useState(true);

  // PWA Install Prompt Capture
  useEffect(() => {
    const handler = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      window.deferredPrompt = e;
      // Dispatch custom event so listeners know install is available
      window.dispatchEvent(new Event('pwa-install-available'));
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    // Artificial delay to ensure smooth transition
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  if (isInitializing) {
    return (
      <div className="fixed inset-0 bg-[#020617] flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-6 animate-in fade-in zoom-in duration-500">
           <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-indigo-500/20">
              <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin"></div>
           </div>
           <div className="flex flex-col items-center gap-1">
             <h2 className="text-white text-sm font-bold tracking-[0.3em] uppercase opacity-90">Initializing</h2>
             <div className="w-12 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="w-1/2 h-full bg-indigo-500 animate-[loading_1.5s_infinite_ease-in-out]"></div>
             </div>
           </div>
        </div>
      </div>
    );
  }
  return (
    <BrowserRouter>
      <ToastContainer position="top-right" autoClose={3000} />
      <DemoHandler />

      <Routes>
        <Route path="/" element={<AuthPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin"
          element={
            <ProtectedRoute>
              <Layout>
                <SuperAdminPanel />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-tenant-dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <SuperTenantDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student-info"
          element={
            <StudentRoute>
              <Layout>
                <StudentOnboarding />
              </Layout>
            </StudentRoute>
          }
        />
        <Route path="/activation-steps" element={<ActivationSteps />} />
        <Route path="/instituteppt" element={<SchoolPresentation />} />
        <Route path="/bplan" element={<BPlanPresentation />} />
        
        {/* LMS Routes */}
        <Route path="/:instituteId/lms" element={<ProtectedRoute><ThemeProvider><LMSLayout /></ThemeProvider></ProtectedRoute>}>
          <Route index element={<LMSDashboard />} />
          <Route path="cms" element={<CMS />} />
          <Route path="cms-test" element={<CMSTest />} />

          <Route path="student" element={<StudentDashboard />} />
          <Route path="teacher" element={<TeacherPanel />} />
          <Route path="tests" element={<TestEngine />} />
          <Route path="test-live/:testId" element={<LiveTest />} />
          <Route path="test-cba/:testId" element={<CBATest />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="parent" element={<ParentDashboard />} />
          <Route path="payments" element={<Payments />} />
          <Route path="batches" element={<Batches />} />
          <Route path="doubts" element={<DoubtSolving />} />
          <Route path="personalization" element={<Personalization />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="upcoming-classes" element={<UpcomingClasses />} />
          <Route path="live-class/:batchId/:meetingRoom" element={<LiveMeeting />} />
          <Route path="pensense-dashboard" element={<Dashboard />} />
          <Route path="profile" element={<StudentOnboarding />} />
          <Route path="enquiries" element={<Enquiries />} />
          <Route path="activation-videos" element={<ActivationSteps />} />
        </Route>
        {/* Catch-all route to redirect back to home if path not found */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
